/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: RoleProfileFormView.tsx
 ??? Created: 2025-01-05
 ?? Description:
    Role profile creation and editing form with comprehensive permissions
    management and role-based access control.

 ?? Notes:
    - Refactored to use modular hooks and components
    - Implements hierarchical permissions system with expand/collapse
    - Supports module-specific permissions and inheritance
    - Uses custom tooltips for enhanced UX
    - Integrates with role-based access control system
    - Implements comprehensive form validation

 ?? Last Updated: 2025-01-XX by Rodrigo Cuadra (Refactored)
----------------------------------------------------------------*/

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, AlertCircle, Maximize2, Minimize2, Copy, Users, Shield, AlertTriangle, Plus } from 'lucide-react';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useAuthStore } from '../../store/auth';
import { useDebugMode } from '../../hooks/useDebugMode';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { navigateToRoleProfileDuplicate } from './roleProfilesActions';
import { deleteRoleProfile } from './api/roleProfilesApi';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { RoleProfile as RoleProfileType } from './types';
import {
  useRoleProfileForm,
  useRoleProfileData,
  useQuickList,
  useRoleProfilePermissions,
  useRoleProfileSubmit,
  usePermissionsUI,
} from './hooks';
import { renderIcon } from './utils';
import { RoleProfileBasicInfoBox } from './components/RoleProfileBasicInfoBox';
import { RoleProfilePermissionsMatrix } from './components/RoleProfilePermissionsMatrix';

export default function RoleProfileFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const { user } = useAuthStore();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { isActive: debugModeActive } = useDebugMode();
  const tenantSlug = user?.tenant?.slug || t('profiles.defaultTenant');

  const duplicateId = searchParams.get('duplicate');
  const resetParam = searchParams.get('_reset');
  const mode = id === 'new' || duplicateId ? 'create' : 'edit';

  // Hook to handle fixed action bar
  useFixedActionBar(true);

  // Available permission levels with their display properties
  const permissionLevels = [
    { value: 'FULL', label: t('profiles.fullControl'), color: 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20' },
    { value: 'READ', label: t('profiles.readOnly'), color: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20' },
    { value: 'NONE', label: t('profiles.noAccess'), color: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20' }
  ];

  // Form state management
  const {
    formData,
    originalData,
    setFormData,
    setOriginalData,
    handleInputChange,
    resetForm,
    hasChanges: formHasChanges,
    errors,
    setErrors,
  } = useRoleProfileForm(mode);

  // Load profile data and menu items
  const { menuItems, loading } = useRoleProfileData(
    id,
    duplicateId,
    mode,
    setFormData,
    setOriginalData,
    resetParam,
  );

  // Permissions management
  const {
    getPermissionLevel,
    hasInheritedPermission,
    handlePermissionChange,
    generateAllPermissionsFromMenu,
  } = useRoleProfilePermissions(formData, menuItems);

  // Permissions UI state
  const {
    expandedSections,
    expandedGroups,
    searchQuery,
    setSearchQuery,
    toggleSection,
    toggleGroup,
    handleToggleAll,
    areAllExpanded,
    organizeMenuItems,
  } = usePermissionsUI(menuItems, mode);

  // Quick list
  const [showQuickList, setShowQuickList] = useState(false);
  const { quickListSearch, setQuickListSearch, filteredQuickList, isQuickListLoading } = useQuickList(showQuickList);

  // Form submission
  const { saving, errors: submitErrors, setErrors: setSubmitErrors, handleSubmit } = useRoleProfileSubmit(
    mode,
    id,
    formData,
    formHasChanges,
    generateAllPermissionsFromMenu,
    setOriginalData,
  );

  // Handle save internal (wraps handleSubmit to return RoleProfileType)
  const handleSaveInternal = useCallback(async (): Promise<RoleProfileType | undefined> => {
    const savedData = await handleSubmit();

    return savedData;
  }, [handleSubmit, formHasChanges, showInfo, t, mode, id, formData]);

  // Save with action hook
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/role-profiles',
    newPath: '/role-profiles/new',
    getEditPath: (savedData) => `/role-profiles/${savedData.id}`,
    mode,
    storageKey: 'role-profiles-save-action',
  });

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Clear page title to show tenant logo + name in header

  // Handle duplicate from form
  const handleDuplicateFromForm = useCallback(() => {
    if (mode === 'edit' && id) {
      navigateToRoleProfileDuplicate(navigate, id);
    }
  }, [mode, id, navigate]);

  // Navigate to list (used by "List" button in header)
  const handleGoToList = useCallback(() => {
    navigate('/role-profiles');
  }, [navigate]);

  // Standard: Cancel resets form to original values (does NOT navigate)
  const handleCancel = useCallback(() => {
    if (mode === 'create') {
      // Reinitialize permissions for create mode using slugs
      const leafSlugs: string[] = [];
      menuItems.forEach(item => {
        if (item.level === 3 && item.slug) {
          leafSlugs.push(item.slug);
        } else if (item.level === 2 && item.slug) {
          const hasChildren = menuItems.some(
            child => child.parentId === item.id && child.level === 3
          );
          if (!hasChildren) {
            leafSlugs.push(item.slug);
          }
        }
      });

      const allPermissions = leafSlugs.map(slug => ({
        moduleSlug: slug,
        level: 'NONE' as const
      }));

      setFormData({
        name: '',
        description: '',
        permissions: allPermissions
      });
    } else {
      resetForm(originalData, originalData);
    }
    setSubmitErrors({});
  }, [mode, menuItems, setFormData, resetForm, originalData, setSubmitErrors]);

  // Standard: Delete item with confirmation, then navigate to list
  const handleDeleteClick = useCallback(() => {
    if (mode !== 'edit' || !id) return;
    setShowDeleteModal(true);
  }, [mode, id]);

  const handleDeleteConfirm = useCallback(async () => {
    if (mode !== 'edit' || !id) return;

    setIsDeleting(true);
    try {
      await deleteRoleProfile(id, tenantSlug, user?.id);
      showSuccess(t('profiles.profileDeleted'));
      setTimeout(() => {
        navigate('/role-profiles');
      }, 500);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      showError(msg || t('profiles.errorDeleting'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [mode, id, tenantSlug, user?.id, showSuccess, showError, navigate, t]);

  // Combine errors from form and submit
  const allErrors = { ...errors, ...submitErrors };

  return (
    <div className="p-6">
      {/* Fixed Header */}
      <ModuleFormHeader
        icon={<Users className="h-5 w-5 text-primary" />}
        title={mode === 'create' ? t('profiles.createProfile') : `${t('profiles.editProfile')} - ${formData.name || ''}`}
        backButtonText={t('common.list')}
        onBack={handleGoToList}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('profiles.quickList'),
            onClick: () => setShowQuickList(true)
          },
          ...(mode === 'edit' && id && id !== 'new' && !duplicateId
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('common.duplicate'),
                onClick: handleDuplicateFromForm
              }
            ]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('profiles.addProfile'),
            onClick: () => navigate('/role-profiles/new')
          }
        ]}
      />

      {/* Form */}
      <div className="mt-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <RoleProfileBasicInfoBox
            name={formData.name}
            description={formData.description || ''}
            isSystem={formData.is_system}
            debugModeActive={debugModeActive}
            errors={allErrors}
            onNameChange={(v) => handleInputChange('name', v)}
            onDescriptionChange={(v) => handleInputChange('description', v)}
            t={t}
          />

                    {/* Permissions Matrix */}
          <RoleProfilePermissionsMatrix
            loading={loading}
            organizeMenuItems={organizeMenuItems}
            permissionLevels={permissionLevels}
            expandedSections={expandedSections}
            expandedGroups={expandedGroups}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            toggleSection={toggleSection}
            toggleGroup={toggleGroup}
            handleToggleAll={handleToggleAll}
            areAllExpanded={areAllExpanded}
            getPermissionLevel={getPermissionLevel}
            hasInheritedPermission={hasInheritedPermission}
            handlePermissionChange={handlePermissionChange}
            setFormData={setFormData}
            t={t}
          />
                </form>
      </div>

      {/* Quick List Modal */}
      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('profiles.quickList', 'Role Profiles')}
        items={filteredQuickList.filter((p): p is typeof p & { id: string } => !!p.id)}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(profile) => {
          setShowQuickList(false);
          navigate(`/role-profiles/${profile.id}`);
        }}
        currentItemId={mode === 'edit' && !duplicateId ? id : null}
        renderItem={(profile, isCurrent) => (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{profile.name}</p>
              {profile.description && (
                <p className="text-sm text-muted-foreground">{profile.description}</p>
              )}
            </div>
            {isCurrent && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                {t('profiles.current', 'Current')}
              </span>
            )}
          </div>
        )}
        searchPlaceholder={t('profiles.quickListSearch', 'Search by name or description')}
        emptyMessage={t('profiles.quickListPrompt', 'Start typing to search...')}
        noResultsMessage={t('profiles.quickListNoResults', 'No profiles found')}
        headerIcon={<Shield className="h-5 w-5" />}
        minSearchChars={0}
      />

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={handleDeleteClick}
        isEditMode={mode === 'edit'}
        isSaving={saving}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('profiles.delete')}
        deleteDisabled={formData.is_system}
        deleteTooltip={formData.is_system ? t('profiles.messages.cannotDeleteSystem') : undefined}
        hasChanges={formHasChanges}
        useSaveDropdown={true}
        showSave={true}
        saveDropdownStorageKey="role-profiles-save-action"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('profiles.deleteConfirmTitle')}
        message={t('profiles.deleteConfirmMessage')}
        confirmText={t('profiles.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
